// This module owns navigation and editorial image viewing only. Shopify and the
// existing product scripts remain the single authority for all commerce state.
export function initializeProductStory(root, { doc = globalThis.document, win = globalThis.window } = {}) {
  if (!root || root.dataset.storyReady === 'true') return null;
  root.dataset.storyReady = 'true';
  const nav = root.querySelector('[data-story-nav]');
  const chapters = [...root.querySelectorAll('[data-story-chapter]')];
  const tabs = [...root.querySelectorAll('[data-story-tab]')];
  const dialog = root.querySelector('[data-story-zoom-dialog]');
  const image = dialog?.querySelector('[data-story-zoom-image]');
  const media = dialog?.querySelector('[data-story-zoom-media]');
  const caption = dialog?.querySelector('[data-story-zoom-caption]');
  const closeButton = dialog?.querySelector('[data-story-zoom-close]');
  let opener = null;
  let frame = null;
  let destroyed = false;
  let chapterInset = 84;
  const reducedMotion = win.matchMedia?.('(prefers-reduced-motion: reduce)');

  const update = () => {
    frame = null;
    if (destroyed || !nav) return;
    const header = doc.querySelector('.site-header');
    const position = header ? win.getComputedStyle(header).position : '';
    const headerOffset = ['fixed', 'sticky'].includes(position) ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    root.style.setProperty('--story-header-offset', `${headerOffset}px`);
    root.style.setProperty('--story-nav-height', `${Math.ceil(nav.getBoundingClientRect().height)}px`);
    chapterInset = headerOffset + Math.ceil(nav.getBoundingClientRect().height) + 20;
    const threshold = headerOffset + nav.getBoundingClientRect().height + 60;
    let current = chapters[0];
    for (const chapter of chapters) if (chapter.getBoundingClientRect().top <= threshold) current = chapter;
    for (const tab of tabs) {
      if (tab.dataset.storyTab === current?.dataset.storyChapter) tab.setAttribute('aria-current', 'location');
      else tab.removeAttribute('aria-current');
    }
  };
  const schedule = () => {
    if (frame !== null || destroyed) return;
    frame = win.requestAnimationFrame(update);
  };
  const restore = () => { const target = opener; opener = null; if (target?.isConnected) target.focus({ preventScroll: true }); };
  const close = () => { if (dialog?.open) dialog.close(); };
  const onClick = event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = event.target.closest?.('[data-story-anchor]');
    if (anchor && root.contains(anchor)) {
      const hash = anchor.getAttribute('href');
      if (!/^#Heir(?:Overview|Details|Purchase)-[A-Za-z0-9_-]+$/.test(hash || '')) return;
      const target = doc.getElementById(hash.slice(1));
      if (!target || !root.contains(target)) return;
      event.preventDefault();
      // Measure now, not on the next resize frame. The nav changes from one row
      // to two on phones and may have resized since the last scroll event.
      update();
      target.focus({ preventScroll: true });
      const behavior = reducedMotion?.matches ? 'instant' : 'smooth';
      if (typeof win.scrollTo === 'function') {
        const top = Math.max(0, (win.scrollY || 0) + target.getBoundingClientRect().top - chapterInset);
        win.scrollTo({ top, behavior });
      } else target.scrollIntoView({ behavior, block: 'start' });
      try { win.history.replaceState(win.history.state, '', hash); } catch { /* Anchor target still works. */ }
      schedule();
      return;
    }
    const trigger = event.target.closest?.('[data-story-zoom]');
    if (!trigger || !root.contains(trigger) || !dialog || !image || !media || !caption || typeof dialog.showModal !== 'function' || doc.querySelector('dialog[open]')) return;
    const src = trigger.getAttribute('href');
    let url;
    try { url = new URL(src, win.location.href); } catch { return; }
    if (!['http:', 'https:'].includes(url.protocol)) return;
    const crop = ['top-left', 'top-right'].includes(trigger.dataset.storyCrop) ? trigger.dataset.storyCrop : '';
    const width = Math.max(1, Number(trigger.dataset.storyWidth) || 1);
    const height = Math.max(1, Number(trigger.dataset.storyHeight) || 1);
    image.src = url.href;
    image.alt = trigger.dataset.storyAlt || '';
    image.width = width;
    image.height = height;
    caption.textContent = image.alt;
    media.className = `heir-story__media${crop ? ` heir-story__media--${crop}` : ''}`;
    media.style.setProperty('--story-image-ratio', String(width / height));
    try { dialog.showModal(); } catch { return; }
    opener = trigger;
    event.preventDefault();
    closeButton?.focus();
  };
  const backdrop = event => { if (event.target === dialog) close(); };
  root.addEventListener('click', onClick);
  closeButton?.addEventListener('click', close);
  dialog?.addEventListener('close', restore);
  dialog?.addEventListener('click', backdrop);
  win.addEventListener('scroll', schedule, { passive: true });
  win.addEventListener('resize', schedule, { passive: true });
  const resize = typeof win.ResizeObserver === 'function' ? new win.ResizeObserver(schedule) : null;
  if (nav) resize?.observe(nav);
  update();
  return { destroy() {
    destroyed = true;
    if (frame !== null) win.cancelAnimationFrame(frame);
    resize?.disconnect();
    root.removeEventListener('click', onClick);
    closeButton?.removeEventListener('click', close);
    dialog?.removeEventListener('close', restore);
    dialog?.removeEventListener('click', backdrop);
    win.removeEventListener('scroll', schedule);
    win.removeEventListener('resize', schedule);
    close(); restore();
    delete root.dataset.storyReady;
  } };
}

if (typeof document !== 'undefined') {
  const mounted = new WeakMap();
  const mount = scope => {
    const roots = scope.matches?.('[data-product-story]') ? [scope] : scope.querySelectorAll('[data-product-story]');
    for (const root of roots) if (!mounted.has(root)) mounted.set(root, initializeProductStory(root));
  };
  mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const root of event.target.querySelectorAll('[data-product-story]')) { mounted.get(root)?.destroy(); mounted.delete(root); }
  });
}
